/**
 * Configuration constants used for D3 visualizations,
 * covering settings for zoom behavior, polygon appearance, and hole styling.
 * These constants offer a single, convenient place to manage visualization parameters
 * and help maintain consistency across all visualizations.
 */

/** Hole rendering state: normal, hovered, or selected */
type HoleState = 'normal' | 'hovered' | 'selected';

/** Hole validity: invalid (missing coordinates) or valid */
type HoleValidity = 'invalid' | 'valid';

/** Hole style attributes: radius multiplier, fill, and stroke color */
type HoleStyleAttributes = {
  /** Stroke color: CSS variable or 'transparent' */
  stroke: string;
};

const VISUALIZATION_CONFIG = {
  /* Configuration for zoom behavior */
  ZOOM: {
    /** Minimum zoom level */
    min: 0.025,
    /** Maximum zoom level */
    max: 20,
  },
  /* Configuration for the polygon visualization */
  POLYGON: {
    /** Opacity of the polygon fill */
    FILL_OPACITY: 0.25,
  },
  /* Configuration for block model labels */
  BLOCK_LABELS: {
    /** Font size in SVG units (scales naturally with zoom) */
    FONT_SIZE: 4,
    /** Font weight for labels */
    FONT_WEIGHT: 600,
  },
  /* Configuration for the hole visualization */
  HOLE: {
    /**
     * Hole style attributes by validity and state.
     * Lookup: HOLE_STYLES[isInvalid ? 'invalid' : 'valid'][state]
     */
    STYLES: {
      invalid: {
        normal: { stroke: 'var(--color-warning)' },
        hovered: { stroke: 'var(--color-warning)' },
        selected: { stroke: 'var(--color-warning)' },
      },
      valid: {
        normal: { stroke: 'transparent' },
        hovered: { stroke: 'transparent' },
        selected: { stroke: 'var(--color-primary)' },
      },
    } as const satisfies Record<HoleValidity, Record<HoleState, HoleStyleAttributes>>,
  },
} as const;

export { type HoleState, type HoleStyleAttributes, type HoleValidity, VISUALIZATION_CONFIG };
